import type {
  MAMManager,
  MAMGroup,
  AllocationRule,
  MAMClientAccount,
  MasterTrade,
  ChildTrade,
  MAMPerformancePoint,
  MAMCommissionRecord,
  MAMRiskMetrics,
  MAMReport,
  MAMNotification,
  MAMSettings,
  MAMAuditLog,
  MAMDashboardKPIs,
  ExecutionTimelineEvent,
} from '../types';

export const MOCK_MAM_KPIS: MAMDashboardKPIs = {
  totalVolumeLots: 14850.5,
  totalVolumeChange: 14.2,
  totalClients: 342,
  totalClientsChange: 8.5,
  openTradesCount: 48,
  openTradesChange: -2.1,
  totalProfit: 284500.75,
  totalProfitChange: 18.9,
  totalCommission: 42680.0,
  totalCommissionChange: 12.4,
  riskScore: 28,
  activeManagers: 12,
  totalAUM: 4850000.0,
  avgWinRate: 68.4,
};

export const MOCK_MAM_MANAGERS: MAMManager[] = [
  {
    id: 'mgr-101',
    name: 'Apex Capital FX',
    email: 'alex.vance@apexcapital.io',
    accountNumber: 'MT5-998811',
    avatar:
      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150',
    status: 'active',
    totalAUM: 1850000,
    activeClients: 124,
    groupsCount: 3,
    performanceFee: 20,
    managementFee: 2.0,
    totalReturn: 42.8,
    monthlyReturn: 5.6,
    maxDrawdown: 11.2,
    winRate: 72.5,
    totalTrades: 1420,
    riskLevel: 'medium',
    leverage: '1:200',
    tradingSymbols: ['EURUSD', 'GBPUSD', 'XAUUSD'],
    joinedAt: '2025-01-15T10:00:00Z',
    lastTradeAt: '2026-07-29T11:45:00Z',
  },
  {
    id: 'mgr-102',
    name: 'Quantum Alpha Trading',
    email: 'sarah.chen@quantumalpha.com',
    accountNumber: 'MT5-882233',
    avatar:
      'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150',
    status: 'active',
    totalAUM: 1420000,
    activeClients: 98,
    groupsCount: 2,
    performanceFee: 25,
    managementFee: 1.5,
    totalReturn: 68.4,
    monthlyReturn: 8.2,
    maxDrawdown: 14.8,
    winRate: 66.8,
    totalTrades: 2150,
    riskLevel: 'high',
    leverage: '1:500',
    tradingSymbols: ['XAUUSD', 'US30', 'BTCUSD'],
    joinedAt: '2025-03-10T14:30:00Z',
    lastTradeAt: '2026-07-29T12:10:00Z',
  },
  {
    id: 'mgr-103',
    name: 'Helios Conservative Pool',
    email: 'marcus.ross@helios.net',
    accountNumber: 'MT5-774411',
    avatar:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
    status: 'active',
    totalAUM: 980000,
    activeClients: 75,
    groupsCount: 2,
    performanceFee: 15,
    managementFee: 1.0,
    totalReturn: 18.5,
    monthlyReturn: 2.1,
    maxDrawdown: 4.5,
    winRate: 81.2,
    totalTrades: 680,
    riskLevel: 'low',
    leverage: '1:100',
    tradingSymbols: ['EURUSD', 'USDJPY', 'USDCAD'],
    joinedAt: '2024-11-01T09:00:00Z',
    lastTradeAt: '2026-07-29T10:20:00Z',
  },
  {
    id: 'mgr-104',
    name: 'Vanguard Multi-Asset',
    email: 'david.wright@vanguardfx.com',
    accountNumber: 'MT5-661122',
    avatar:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150',
    status: 'pending',
    totalAUM: 600000,
    activeClients: 45,
    groupsCount: 1,
    performanceFee: 20,
    managementFee: 2.0,
    totalReturn: 12.0,
    monthlyReturn: 1.8,
    maxDrawdown: 8.0,
    winRate: 62.0,
    totalTrades: 320,
    riskLevel: 'medium',
    leverage: '1:200',
    tradingSymbols: ['AUDUSD', 'GBPUSD'],
    joinedAt: '2026-06-01T11:00:00Z',
  },
];

export const MOCK_MAM_GROUPS: MAMGroup[] = [
  {
    id: 'grp-01',
    name: 'Apex Pro Equity Pool',
    description:
      'Proportional equity allocation pool for high net worth clients.',
    managerId: 'mgr-101',
    managerName: 'Apex Capital FX',
    allocationMethod: 'equity_ratio',
    status: 'active',
    totalClients: 64,
    totalEquity: 1250000,
    totalBalance: 1200000,
    minDeposit: 10000,
    maxClients: 100,
    leverage: '1:200',
    executionMode: 'market',
    createdAt: '2025-01-20T12:00:00Z',
    updatedAt: '2026-07-28T16:00:00Z',
  },
  {
    id: 'grp-02',
    name: 'Quantum Fixed Lot Aggressive',
    description:
      'Fixed lot allocation strategy for high risk appetite accounts.',
    managerId: 'mgr-102',
    managerName: 'Quantum Alpha Trading',
    allocationMethod: 'fixed_lot',
    status: 'active',
    totalClients: 52,
    totalEquity: 890000,
    totalBalance: 850000,
    minDeposit: 5000,
    maxClients: 80,
    leverage: '1:500',
    executionMode: 'instant',
    createdAt: '2025-03-15T09:00:00Z',
    updatedAt: '2026-07-29T10:00:00Z',
  },
  {
    id: 'grp-03',
    name: 'Helios Low Drawdown Balance',
    description: 'Balance ratio allocation focusing on capital preservation.',
    managerId: 'mgr-103',
    managerName: 'Helios Conservative Pool',
    allocationMethod: 'balance_ratio',
    status: 'active',
    totalClients: 48,
    totalEquity: 680000,
    totalBalance: 670000,
    minDeposit: 2500,
    maxClients: 100,
    leverage: '1:100',
    executionMode: 'market',
    createdAt: '2024-11-10T10:00:00Z',
    updatedAt: '2026-07-29T08:00:00Z',
  },
  {
    id: 'grp-04',
    name: 'Multi-Asset Risk Proportional',
    description:
      'Risk-based lot sizing computed automatically per account risk setting.',
    managerId: 'mgr-101',
    managerName: 'Apex Capital FX',
    allocationMethod: 'risk_based',
    status: 'active',
    totalClients: 38,
    totalEquity: 600000,
    totalBalance: 590000,
    minDeposit: 5000,
    maxClients: 50,
    leverage: '1:200',
    executionMode: 'market',
    createdAt: '2025-05-01T14:00:00Z',
    updatedAt: '2026-07-27T11:00:00Z',
  },
];

export const MOCK_ALLOCATION_RULES: AllocationRule[] = [
  {
    id: 'rule-1',
    name: 'Standard Balance Ratio',
    type: 'balance_ratio',
    description:
      'Allocates trade volumes strictly proportional to account balance.',
    parameters: {
      minLotSize: 0.01,
      maxLotSize: 50.0,
      roundLots: 'nearest',
    },
    isDefault: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
  },
  {
    id: 'rule-2',
    name: 'Equity Ratio Dynamic',
    type: 'equity_ratio',
    description:
      'Adjusts lot sizes based on live equity including open floating profit/loss.',
    parameters: {
      minLotSize: 0.01,
      maxLotSize: 100.0,
      roundLots: 'down',
    },
    isDefault: false,
    createdAt: '2025-02-10T00:00:00Z',
    updatedAt: '2025-02-10T00:00:00Z',
  },
  {
    id: 'rule-3',
    name: 'Fixed 0.1 Lot Per Account',
    type: 'fixed_lot',
    description: 'Every sub-account receives an identical 0.10 lot position.',
    parameters: {
      fixedLotValue: 0.1,
      minLotSize: 0.1,
      maxLotSize: 0.1,
      roundLots: 'exact',
    },
    isDefault: false,
    createdAt: '2025-03-01T00:00:00Z',
    updatedAt: '2025-03-01T00:00:00Z',
  },
  {
    id: 'rule-4',
    name: 'Custom Multiplier (1.5x)',
    type: 'multiplier',
    description:
      'Scales sub-account trade size by 1.5 times the master lot calculation.',
    parameters: {
      multiplierFactor: 1.5,
      minLotSize: 0.01,
      maxLotSize: 20.0,
    },
    isDefault: false,
    createdAt: '2025-04-12T00:00:00Z',
    updatedAt: '2025-04-12T00:00:00Z',
  },
  {
    id: 'rule-5',
    name: 'Fixed Percentage Share (10%)',
    type: 'percentage',
    description:
      'Sub-account gets a fixed 10% slice of master trade total volume.',
    parameters: {
      minLotSize: 0.01,
    },
    isDefault: false,
    createdAt: '2025-05-18T00:00:00Z',
    updatedAt: '2025-05-18T00:00:00Z',
  },
  {
    id: 'rule-6',
    name: 'Risk-Based 2% Stop Loss Cap',
    type: 'risk_based',
    description:
      'Limits individual trade lot size to max 2% account equity loss.',
    parameters: {
      riskPercent: 2.0,
      minLotSize: 0.01,
      maxLotSize: 10.0,
    },
    isDefault: false,
    createdAt: '2025-06-20T00:00:00Z',
    updatedAt: '2025-06-20T00:00:00Z',
  },
];

export const MOCK_CLIENT_ACCOUNTS: MAMClientAccount[] = [
  {
    id: 'cli-101',
    accountNumber: 'ACC-88001',
    clientName: 'Michael Scott',
    email: 'm.scott@paper.com',
    country: 'United States',
    groupId: 'grp-01',
    groupName: 'Apex Pro Equity Pool',
    managerName: 'Apex Capital FX',
    equity: 54200,
    balance: 50000,
    margin: 1200,
    freeMargin: 53000,
    allocatedLots: 4.34,
    copyRatio: 1.0,
    status: 'active',
    joinedDate: '2025-02-01T10:00:00Z',
    unrealizedPnL: 4200,
    realizedPnL: 12800,
  },
  {
    id: 'cli-102',
    accountNumber: 'ACC-88002',
    clientName: 'Elena Rostova',
    email: 'elena.r@fintech.de',
    country: 'Germany',
    groupId: 'grp-01',
    groupName: 'Apex Pro Equity Pool',
    managerName: 'Apex Capital FX',
    equity: 128500,
    balance: 120000,
    margin: 3400,
    freeMargin: 125100,
    allocatedLots: 10.28,
    copyRatio: 1.0,
    status: 'active',
    joinedDate: '2025-02-15T14:20:00Z',
    unrealizedPnL: 8500,
    realizedPnL: 34200,
  },
  {
    id: 'cli-103',
    accountNumber: 'ACC-88003',
    clientName: 'Hiroshi Tanaka',
    email: 'h.tanaka@tokyo.jp',
    country: 'Japan',
    groupId: 'grp-02',
    groupName: 'Quantum Fixed Lot Aggressive',
    managerName: 'Quantum Alpha Trading',
    equity: 32000,
    balance: 30000,
    margin: 1800,
    freeMargin: 30200,
    allocatedLots: 0.5,
    copyRatio: 1.0,
    status: 'active',
    joinedDate: '2025-03-20T09:00:00Z',
    unrealizedPnL: 2000,
    realizedPnL: 9400,
  },
  {
    id: 'cli-104',
    accountNumber: 'ACC-88004',
    clientName: 'Arthur Pendelton',
    email: 'arthur.p@wealth.co.uk',
    country: 'United Kingdom',
    groupId: 'grp-03',
    groupName: 'Helios Low Drawdown Balance',
    managerName: 'Helios Conservative Pool',
    equity: 76400,
    balance: 75000,
    margin: 950,
    freeMargin: 75450,
    allocatedLots: 2.25,
    copyRatio: 1.0,
    status: 'active',
    joinedDate: '2024-12-01T11:00:00Z',
    unrealizedPnL: 1400,
    realizedPnL: 8900,
  },
  {
    id: 'cli-105',
    accountNumber: 'ACC-88005',
    clientName: 'Sophia Al-Mansoor',
    email: 'sophia@gulfinvest.ae',
    country: 'United Arab Emirates',
    groupId: 'grp-01',
    groupName: 'Apex Pro Equity Pool',
    managerName: 'Apex Capital FX',
    equity: 210000,
    balance: 200000,
    margin: 4500,
    freeMargin: 205500,
    allocatedLots: 16.8,
    copyRatio: 1.0,
    status: 'active',
    joinedDate: '2025-04-10T16:00:00Z',
    unrealizedPnL: 10000,
    realizedPnL: 48000,
  },
  {
    id: 'cli-106',
    accountNumber: 'ACC-88006',
    clientName: 'Carlos Santos',
    email: 'carlos.s@rio.br',
    country: 'Brazil',
    groupId: 'grp-02',
    groupName: 'Quantum Fixed Lot Aggressive',
    managerName: 'Quantum Alpha Trading',
    equity: 15400,
    balance: 18000,
    margin: 1200,
    freeMargin: 14200,
    allocatedLots: 0.5,
    copyRatio: 1.0,
    status: 'paused',
    joinedDate: '2025-05-02T13:00:00Z',
    unrealizedPnL: -2600,
    realizedPnL: 1200,
  },
];

export const MOCK_MASTER_TRADES: MasterTrade[] = [
  {
    id: 'trd-1001',
    ticket: '8899201',
    managerId: 'mgr-101',
    managerName: 'Apex Capital FX',
    groupId: 'grp-01',
    groupName: 'Apex Pro Equity Pool',
    symbol: 'EURUSD',
    direction: 'buy',
    masterVolume: 10.0,
    allocatedVolume: 98.4,
    openPrice: 1.0845,
    currentPrice: 1.0892,
    stopLoss: 1.08,
    takeProfit: 1.095,
    profit: 4700.0,
    swap: -12.5,
    commission: -45.0,
    status: 'open',
    executionStatus: 'executed',
    openTime: '2026-07-29T10:15:00Z',
    childCount: 64,
    failedChildCount: 0,
    executionTimeMs: 142,
    comment: 'Breakout pattern buy',
  },
  {
    id: 'trd-1002',
    ticket: '8899202',
    managerId: 'mgr-102',
    managerName: 'Quantum Alpha Trading',
    groupId: 'grp-02',
    groupName: 'Quantum Fixed Lot Aggressive',
    symbol: 'XAUUSD',
    direction: 'sell',
    masterVolume: 5.0,
    allocatedVolume: 26.0,
    openPrice: 2385.5,
    currentPrice: 2378.2,
    stopLoss: 2400.0,
    takeProfit: 2350.0,
    profit: 3650.0,
    swap: -8.0,
    commission: -30.0,
    status: 'open',
    executionStatus: 'executed',
    openTime: '2026-07-29T11:00:00Z',
    childCount: 52,
    failedChildCount: 0,
    executionTimeMs: 185,
    comment: 'Gold resistance rejection',
  },
  {
    id: 'trd-1003',
    ticket: '8899203',
    managerId: 'mgr-103',
    managerName: 'Helios Conservative Pool',
    groupId: 'grp-03',
    groupName: 'Helios Low Drawdown Balance',
    symbol: 'GBPUSD',
    direction: 'buy',
    masterVolume: 4.0,
    allocatedVolume: 15.6,
    openPrice: 1.288,
    currentPrice: 1.2865,
    stopLoss: 1.283,
    takeProfit: 1.295,
    profit: -600.0,
    swap: -3.2,
    commission: -20.0,
    status: 'open',
    executionStatus: 'executed',
    openTime: '2026-07-29T11:40:00Z',
    childCount: 48,
    failedChildCount: 0,
    executionTimeMs: 110,
    comment: 'Pound momentum entry',
  },
  {
    id: 'trd-1004',
    ticket: '8899204',
    managerId: 'mgr-101',
    managerName: 'Apex Capital FX',
    groupId: 'grp-01',
    groupName: 'Apex Pro Equity Pool',
    symbol: 'USDJPY',
    direction: 'sell',
    masterVolume: 8.0,
    allocatedVolume: 78.7,
    openPrice: 154.2,
    closePrice: 152.8,
    profit: 11200.0,
    swap: 45.0,
    commission: -40.0,
    status: 'closed',
    executionStatus: 'executed',
    openTime: '2026-07-28T09:00:00Z',
    closeTime: '2026-07-29T08:30:00Z',
    childCount: 64,
    failedChildCount: 0,
    executionTimeMs: 160,
    comment: 'Yen intervention play',
  },
  {
    id: 'trd-1005',
    ticket: '8899205',
    managerId: 'mgr-102',
    managerName: 'Quantum Alpha Trading',
    groupId: 'grp-02',
    groupName: 'Quantum Fixed Lot Aggressive',
    symbol: 'US30',
    direction: 'buy',
    masterVolume: 2.0,
    allocatedVolume: 10.4,
    openPrice: 40200,
    currentPrice: 40180,
    profit: -400.0,
    status: 'open',
    executionStatus: 'partial',
    openTime: '2026-07-29T12:05:00Z',
    childCount: 52,
    failedChildCount: 3,
    executionTimeMs: 420,
    comment: 'US Index scalping',
  },
];

export const MOCK_CHILD_TRADES: ChildTrade[] = [
  {
    id: 'chd-1',
    masterTradeId: 'trd-1001',
    ticket: '8899201-1',
    accountNumber: 'ACC-88001',
    clientName: 'Michael Scott',
    direction: 'buy',
    allocatedVolume: 4.34,
    openPrice: 1.0845,
    profit: 204.0,
    status: 'open',
    executionStatus: 'executed',
    executionTimeMs: 45,
    retries: 0,
    executedAt: '2026-07-29T10:15:00Z',
  },
  {
    id: 'chd-2',
    masterTradeId: 'trd-1001',
    ticket: '8899201-2',
    accountNumber: 'ACC-88002',
    clientName: 'Elena Rostova',
    direction: 'buy',
    allocatedVolume: 10.28,
    openPrice: 1.0845,
    profit: 483.0,
    status: 'open',
    executionStatus: 'executed',
    executionTimeMs: 52,
    retries: 0,
    executedAt: '2026-07-29T10:15:00Z',
  },
  {
    id: 'chd-3',
    masterTradeId: 'trd-1005',
    ticket: '8899205-18',
    accountNumber: 'ACC-88006',
    clientName: 'Carlos Santos',
    direction: 'buy',
    allocatedVolume: 0.5,
    openPrice: 40200,
    profit: -20.0,
    status: 'failed',
    executionStatus: 'failed',
    executionTimeMs: 1500,
    errorMessage: 'Off quotes / Margin limit exceeded',
    retries: 2,
    executedAt: '2026-07-29T12:05:01Z',
  },
];

export const MOCK_PERFORMANCE_SERIES: MAMPerformancePoint[] = [
  {
    date: 'Jul 01',
    equity: 4100000,
    balance: 4050000,
    roi: 12.5,
    drawdown: 3.2,
    tradesCount: 42,
    volumeLots: 420,
  },
  {
    date: 'Jul 05',
    equity: 4250000,
    balance: 4180000,
    roi: 14.8,
    drawdown: 2.1,
    tradesCount: 58,
    volumeLots: 610,
  },
  {
    date: 'Jul 10',
    equity: 4400000,
    balance: 4320000,
    roi: 17.2,
    drawdown: 4.5,
    tradesCount: 84,
    volumeLots: 890,
  },
  {
    date: 'Jul 15',
    equity: 4350000,
    balance: 4380000,
    roi: 16.1,
    drawdown: 5.8,
    tradesCount: 65,
    volumeLots: 720,
  },
  {
    date: 'Jul 20',
    equity: 4620000,
    balance: 4500000,
    roi: 21.0,
    drawdown: 2.8,
    tradesCount: 92,
    volumeLots: 1100,
  },
  {
    date: 'Jul 25',
    equity: 4780000,
    balance: 4690000,
    roi: 24.5,
    drawdown: 1.9,
    tradesCount: 110,
    volumeLots: 1350,
  },
  {
    date: 'Jul 29',
    equity: 4850000,
    balance: 4760000,
    roi: 26.2,
    drawdown: 2.4,
    tradesCount: 78,
    volumeLots: 980,
  },
];

export const MOCK_COMMISSION_RECORDS: MAMCommissionRecord[] = [
  {
    id: 'com-001',
    managerId: 'mgr-101',
    managerName: 'Apex Capital FX',
    clientAccountId: 'cli-101',
    clientName: 'Michael Scott',
    type: 'performance_fee',
    baseAmount: 12800,
    feeRate: 20,
    commissionAmount: 2560,
    period: '2026 Q2',
    status: 'paid',
    date: '2026-07-01T00:00:00Z',
  },
  {
    id: 'com-002',
    managerId: 'mgr-102',
    managerName: 'Quantum Alpha Trading',
    clientAccountId: 'cli-103',
    clientName: 'Hiroshi Tanaka',
    type: 'performance_fee',
    baseAmount: 9400,
    feeRate: 25,
    commissionAmount: 2350,
    period: '2026 Q2',
    status: 'paid',
    date: '2026-07-01T00:00:00Z',
  },
  {
    id: 'com-003',
    managerId: 'mgr-101',
    managerName: 'Apex Capital FX',
    clientAccountId: 'cli-102',
    clientName: 'Elena Rostova',
    type: 'management_fee',
    baseAmount: 120000,
    feeRate: 2.0,
    commissionAmount: 2400,
    period: '2026 July',
    status: 'pending',
    date: '2026-07-29T00:00:00Z',
  },
];

export const MOCK_RISK_METRICS: MAMRiskMetrics = {
  maxDrawdownLimit: 25,
  dailyLossLimit: 10000,
  maxLeverage: '1:500',
  emergencyStopActive: false,
  activeMarginCallCount: 1,
  symbolRestrictions: ['BTCUSD', 'ETHUSD'],
  maxOpenLotsPerAccount: 50,
  stopOutLevel: 30,
};

export const MOCK_NOTIFICATIONS: MAMNotification[] = [
  {
    id: 'notif-1',
    title: 'Trade Allocation Retry Failed',
    message:
      'Child trade 8899205-18 for Carlos Santos failed due to margin threshold.',
    type: 'warning',
    priority: 'high',
    isRead: false,
    createdAt: '2026-07-29T12:06:00Z',
  },
  {
    id: 'notif-2',
    title: 'New MAM Group Activated',
    message:
      'Group "Multi-Asset Risk Proportional" is now online with 38 clients.',
    type: 'success',
    priority: 'medium',
    isRead: true,
    createdAt: '2026-07-28T14:00:00Z',
  },
  {
    id: 'notif-3',
    title: 'Manager Performance Milestone',
    message: 'Apex Capital FX crossed $1,800,000 in total AUM!',
    type: 'info',
    priority: 'low',
    isRead: true,
    createdAt: '2026-07-27T09:30:00Z',
  },
];

export const MOCK_AUDIT_LOGS: MAMAuditLog[] = [
  {
    id: 'aud-101',
    action: 'execute',
    entity: 'MasterTrade',
    entityId: 'trd-1001',
    description:
      'Master order #8899201 allocated 10.0 lots across 64 sub-accounts',
    performedBy: 'System Engine',
    ipAddress: '127.0.0.1',
    timestamp: '2026-07-29T10:15:01Z',
    status: 'success',
  },
  {
    id: 'aud-102',
    action: 'retry',
    entity: 'ChildTrade',
    entityId: 'chd-3',
    description: 'Manual retry initiated for Child Trade 8899205-18',
    performedBy: 'Admin User (alex@crm.com)',
    ipAddress: '192.168.1.45',
    timestamp: '2026-07-29T12:10:00Z',
    status: 'failed',
  },
  {
    id: 'aud-103',
    action: 'config',
    entity: 'MAMSettings',
    entityId: 'sys-risk',
    description: 'Updated global max drawdown limit from 20% to 25%',
    performedBy: 'Risk Officer',
    ipAddress: '192.168.1.12',
    timestamp: '2026-07-28T11:20:00Z',
    status: 'success',
  },
];

export const MOCK_MAM_SETTINGS: MAMSettings = {
  general: {
    moduleName: 'Multi-Account Manager (MAM)',
    defaultCurrency: 'USD',
    timezone: 'UTC+0',
    maxClientsPerGroup: 200,
    autoAllocationOnOrder: true,
  },
  risk: {
    globalMaxDrawdown: 25,
    dailyLossLimit: 10000,
    emergencyDisconnectThreshold: 35,
    marginCallLevel: 50,
    stopOutLevel: 30,
  },
  execution: {
    defaultExecutionMode: 'market',
    maxSlippagePips: 3,
    retryAttempts: 3,
    retryDelayMs: 500,
  },
  fees: {
    defaultPerformanceFee: 20,
    defaultManagementFee: 2.0,
    payoutSchedule: 'monthly',
  },
};

export const MOCK_MAM_REPORTS: MAMReport[] = [
  {
    id: 'rpt-01',
    title: 'MAM Monthly Performance Report - July 2026',
    type: 'performance',
    format: 'PDF',
    period: 'July 2026',
    generatedAt: '2026-07-29T08:00:00Z',
    generatedBy: 'System Auto-Gen',
    fileSize: '3.4 MB',
    status: 'ready',
  },
  {
    id: 'rpt-02',
    title: 'Master Trade Execution Audit - Q2 2026',
    type: 'trades',
    format: 'CSV',
    period: 'Q2 2026',
    generatedAt: '2026-07-20T10:30:00Z',
    generatedBy: 'Risk Manager',
    fileSize: '8.2 MB',
    status: 'ready',
  },
];

export const MOCK_SIMULATOR_SUB_ACCOUNTS = [
  {
    id: 'sub-1',
    name: 'Michael Scott (ACC-88001)',
    equity: 54200,
    balance: 50000,
  },
  {
    id: 'sub-2',
    name: 'Elena Rostova (ACC-88002)',
    equity: 128500,
    balance: 120000,
  },
  {
    id: 'sub-3',
    name: 'Sophia Al-Mansoor (ACC-88005)',
    equity: 210000,
    balance: 200000,
  },
  {
    id: 'sub-4',
    name: 'Arthur Pendelton (ACC-88004)',
    equity: 76400,
    balance: 75000,
  },
];

export const MOCK_EXECUTION_TIMELINE_EVENTS: ExecutionTimelineEvent[] = [
  {
    id: '1',
    masterTradeId: 'trd-1001',
    stage: 'received',
    message: 'Master order received from MT5 bridge engine',
    timestamp: '2026-07-29T10:15:00Z',
    status: 'info',
  },
  {
    id: '2',
    masterTradeId: 'trd-1001',
    stage: 'calculated',
    message: 'Computed child trade volumes across active sub-accounts',
    timestamp: '2026-07-29T10:15:00.040Z',
    status: 'info',
  },
  {
    id: '3',
    masterTradeId: 'trd-1001',
    stage: 'dispatching',
    message: 'Dispatched orders via MetaTrader API pool',
    timestamp: '2026-07-29T10:15:00.080Z',
    status: 'info',
  },
  {
    id: '4',
    masterTradeId: 'trd-1001',
    stage: 'executed',
    message: 'Child trade executions filled successfully',
    timestamp: '2026-07-29T10:15:00.142Z',
    status: 'success',
  },
];
